/*
  GDriveImportRootSettings — admin-only. Lets an owner/manager lock
  which Drive folder each import scope (Programs, Documents) starts
  from. Staff can never browse above the configured root.

  Two roots are configured independently:
    • programs_root  — used by the exercise-program pickers
    • documents_root — used by Doc Imports folder picker

  Under the hood we just call
    GET  /exercise-programs/gdrive/settings          (status + current roots)
    PUT  /exercise-programs/gdrive/settings/root     ({scope, folder_id})
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Cloud, Folder, FolderLock, Link2, Loader2, RefreshCw, X,
} from "lucide-react";
import GDriveRootPickerDialog from "./GDriveRootPickerDialog";

const SCOPES = [
  {
    key: "programs",
    label: "Programs & Bulk Programs",
    description:
      "Locks the picker used by /program-library, per-client program imports, and Bulk Unassigned imports to this Drive folder.",
    testid: "gdrive-root-programs",
  },
  {
    key: "documents",
    label: "Document Imports (letters, PDFs, images)",
    description:
      "Locks the folder picker used by /doc-imports (PDF/DOCX/JPG/PNG) to this Drive folder.",
    testid: "gdrive-root-documents",
  },
];

export default function GDriveImportRootSettings() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // "programs" | "documents" | null
  const [pastedId, setPastedId] = useState({ programs: "", documents: "" });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/exercise-programs/gdrive/settings");
      setSettings(r.data || {});
    } catch (e) {
      toast.error("Couldn't load Drive settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function saveRoot(scope, folderId) {
    setSaving(scope);
    try {
      const r = await api.put("/exercise-programs/gdrive/settings/root", {
        scope,
        folder_id: folderId || null,
      });
      toast.success(
        folderId
          ? `Locked ${scope} imports to “${r.data?.root?.folder_name || "folder"}”`
          : `Cleared ${scope} import folder — staff can browse all of Drive again.`,
      );
      setPastedId((p) => ({ ...p, [scope]: "" }));
      await refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(null);
    }
  }

  function parseFolderId(input) {
    if (!input) return "";
    const s = input.trim();
    // Extract the folder ID out of a Drive folder URL if pasted:
    //   https://drive.google.com/drive/folders/<id>?...
    //   https://drive.google.com/drive/u/0/folders/<id>?...
    const m = s.match(/\/folders\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : s;
  }

  return (
    <Card
      className="p-4 rounded-lg border border-zinc-200 shadow-none"
      data-testid="gdrive-import-root-settings"
    >
      <div className="mb-4 flex items-start gap-3">
        <div className="rounded-md bg-indigo-50 p-2 mt-0.5">
          <FolderLock className="h-4 w-4 text-indigo-700" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            Google Drive import root folders
          </div>
          <p className="text-sm text-zinc-700 mt-1 max-w-2xl">
            Restrict which part of the connected Drive staff can browse when
            importing programs or documents. Everything outside the folder
            you pick becomes invisible to the picker AND rejected server-side
            — even if a Drive URL is pasted directly.
          </p>
          {settings && !settings.connected && (
            <div className="mt-2 text-[0.75rem] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
              <Cloud className="h-3 w-3" /> Google Drive isn&apos;t connected yet.
              Connect it from a picker first, then come back here.
            </div>
          )}
          {settings?.connected && settings.account_email && (
            <div className="mt-2 text-2xs text-zinc-500">
              Connected as{" "}
              <span className="font-medium text-zinc-800">
                {settings.account_email}
              </span>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="gdrive-root-refresh"
        >
          <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
        </Button>
      </div>

      <div className="space-y-3">
        {SCOPES.map((scope) => {
          const root = settings?.[`${scope.key}_root`];
          const isSaving = saving === scope.key;
          const paste = pastedId[scope.key] || "";
          return (
            <div
              key={scope.key}
              className="rounded-md border border-zinc-200 p-3 bg-white"
              data-testid={scope.testid}
            >
              <div className="flex flex-wrap items-start gap-2 justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900">
                    {scope.label}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 max-w-xl">
                    {scope.description}
                  </div>
                </div>
                {root ? (
                  <div className="text-2xs inline-flex items-center gap-1 rounded bg-emerald-50 text-emerald-800 px-2 py-1 border border-emerald-200"
                       data-testid={`${scope.testid}-current`}>
                    <Folder className="h-3 w-3" />
                    <span className="truncate max-w-[220px]" title={root.folder_name}>
                      {root.folder_name}
                    </span>
                  </div>
                ) : (
                  <div className="text-2xs inline-flex items-center gap-1 rounded bg-zinc-100 text-zinc-600 px-2 py-1 border border-zinc-200">
                    <span>Unrestricted — all of Drive is browsable</span>
                  </div>
                )}
              </div>

              {root && (
                <div className="mt-2 text-2xs text-zinc-500 flex flex-wrap items-center gap-2">
                  <span className="font-mono-num truncate max-w-[280px]"
                        title={root.folder_id}>
                    {root.folder_id}
                  </span>
                  {root.folder_link && (
                    <a
                      href={root.folder_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Link2 className="h-3 w-3" /> Open in Drive
                    </a>
                  )}
                  {root.updated_at && (
                    <span>· updated {new Date(root.updated_at).toLocaleString()}</span>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPickerFor(scope.key)}
                  disabled={!settings?.connected || isSaving}
                  data-testid={`${scope.testid}-browse-btn`}
                >
                  <Folder className="h-3.5 w-3.5 mr-1" />
                  Browse Drive…
                </Button>
                <div className="flex items-center gap-1">
                  <Input
                    placeholder="…or paste folder ID / URL"
                    value={paste}
                    onChange={(e) =>
                      setPastedId((p) => ({ ...p, [scope.key]: e.target.value }))
                    }
                    className="h-8 w-64 text-xs font-mono-num"
                    disabled={!settings?.connected || isSaving}
                    data-testid={`${scope.testid}-paste-input`}
                  />
                  <Button
                    size="sm"
                    onClick={() => saveRoot(scope.key, parseFolderId(paste))}
                    disabled={
                      !settings?.connected ||
                      isSaving ||
                      !parseFolderId(paste)
                    }
                    data-testid={`${scope.testid}-paste-save-btn`}
                  >
                    {isSaving ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Saving…</>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
                {root && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(
                        `Clear the ${scope.key} import folder? Staff will be able to browse ALL of Drive again.`,
                      )) saveRoot(scope.key, null);
                    }}
                    disabled={isSaving}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`${scope.testid}-clear-btn`}
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <GDriveRootPickerDialog
        open={!!pickerFor}
        onOpenChange={(v) => !v && setPickerFor(null)}
        scopeLabel={
          pickerFor
            ? SCOPES.find((s) => s.key === pickerFor)?.label || pickerFor
            : ""
        }
        onPick={async (folder) => {
          if (!pickerFor) return;
          await saveRoot(pickerFor, folder.id);
          setPickerFor(null);
        }}
      />
    </Card>
  );
}
